import { useMemo } from 'react';
import ToggleTabButton from 'src/components/primitives/ToggleTabs/ToggleTabButton';
import { ToggleTab } from 'src/components/primitives/ToggleTabs/types';
import { cm } from 'src/utils/classNames';

const DEFAULT_PADDING = 8;

interface ToggleTabsProps<T extends string> {
	options: ToggleTab<T>[];
	activeTab: T;
	onTabChange: (tab: T) => void;
	/**
	 * Classes for the container.
	 */
	className?: string;
	/**
	 * Padding of the container. Defaults to `4px`.
	 *
	 * Passed separately from `className` because its value is necessary for
	 * calculating the animated background styles.
	 */
	padding?: number;
	/**
	 * Classes for the tab buttons. When a tab is selected, the classes included
	 * in `selectedTabClassName` will take precedence.
	 */
	tabClassName?: string;
	selectedTabClassName?: string;
	animatedBackgroundClassName?: string;
	dataTestId?: string;
	tabTestIdPrefix?: string;
}

export default function ToggleTabs<T extends string>({
	options,
	activeTab,
	onTabChange,
	className,
	padding: paddingFromProps,
	tabClassName,
	selectedTabClassName,
	animatedBackgroundClassName,
	dataTestId,
	tabTestIdPrefix,
}: ToggleTabsProps<T>) {
	const padding = paddingFromProps ?? DEFAULT_PADDING;

	const containerStyles = useMemo(() => {
		return { padding };
	}, [padding]);

	const animatedBackgroundSize = useMemo(() => {
		return {
			width: `calc(${100 / options.length}% - ${padding}px)`,
			height: `calc(100% - ${padding * 2}px)`,
		};
	}, [padding, options.length]);

	const animatedBackgroundStyles = useMemo(() => {
		const selectedTabIndex = options.findIndex(
			(tab) => tab.value === activeTab
		);

		const left = !selectedTabIndex
			? `${padding}px`
			: `${100 * (selectedTabIndex / options.length)}%`;

		return {
			...animatedBackgroundSize,
			left,
		};
	}, [options, activeTab, animatedBackgroundSize, padding]);

	return (
		<div
			className={cm(
				'relative flex w-full items-center rounded-sm border border-button-secondary-bg bg-container-bg',
				className
			)}
			style={containerStyles}
			data-testid={dataTestId}
		>
			{options.map((tab) => (
				<ToggleTabButton
					key={tab.value}
					label={tab.label}
					className={cm(
						tabClassName,
						activeTab === tab.value && selectedTabClassName
					)}
					isActive={activeTab === tab.value}
					onClick={() => onTabChange(tab.value)}
					disabled={tab.disabled}
					data-testid={
						tabTestIdPrefix ? `${tabTestIdPrefix}-${tab.value}` : undefined
					}
				/>
			))}

			{/* Animated background */}
			<div
				className={cm(
					'absolute rounded-xs bg-button-secondary-bg transition-all duration-200 ease-out-smooth',
					animatedBackgroundClassName
				)}
				style={animatedBackgroundStyles}
			/>
		</div>
	);
}
